import { ModuleMenuDto } from '../../models/subscription/subscription-menu.dto';

/** Cookie 4Segments reads to pick up the tenant the user was looking at in 4Comply. */
export const ACTIVE_TENANT_COOKIE = '4suite_active_tenant';

/**
 * Parent domain shared by every deployed host of the suite. Setting the cookie on it is what
 * lets `dashboard*.4comply.io` hand the active tenant to `segments*.4comply.io`; without it the
 * cookie is host-only and 4Segments falls back to its own last-used tenant.
 */
const SHARED_COOKIE_DOMAIN = '.4comply.io';

const DEFAULT_COOKIE_MAX_AGE_SECONDS = 3600;

/** What a click on a module header should do. */
export type ModuleHeaderAction =
  | { kind: 'expand' }
  | { kind: 'open'; route: string }
  | { kind: 'upgrade' };

/**
 * A module with a route is a single destination: the header opens it and has no children.
 * A module without one keeps behaving as an expander.
 */
export function resolveModuleHeaderAction(module: ModuleMenuDto | undefined): ModuleHeaderAction {
  const route = module?.route?.trim();

  if (!route) {
    return { kind: 'expand' };
  }

  // hasAccess is undefined against an API that predates module-level routes: absence is not denial.
  if (module.hasAccess === false) {
    return { kind: 'upgrade' };
  }

  return { kind: 'open', route };
}

/** Mirrors the leaf-item rule: an absolute URL opens in a new tab, anything else is a router route. */
export function isAbsoluteUrl(route: string): boolean {
  return typeof route === 'string' && route.startsWith('http');
}

export interface ActiveTenantCookieOptions {
  maxAgeSeconds?: number;
  /** Adds `Secure`. Left off for http://localhost dev. */
  secure?: boolean;
}

/**
 * Builds the cookie string, in the same shape 4Segments writes and reads it
 * (`4segmentsv2/frontend/src/tenant-cookie.ts`). The domain is only added for suite hosts: a
 * `domain` value without dots is rejected by browsers, so on `localhost` the cookie has to stay
 * host-only — which is enough there, because cookies ignore the port and both apps share the host.
 */
export function activeTenantCookie(
  hostname: string,
  tenantId: string,
  { maxAgeSeconds = DEFAULT_COOKIE_MAX_AGE_SECONDS, secure = false }: ActiveTenantCookieOptions = {},
): string {
  const parts = [
    `${ACTIVE_TENANT_COOKIE}=${encodeURIComponent(tenantId)}`,
    'path=/',
    `max-age=${maxAgeSeconds}`,
    'SameSite=Lax',
  ];

  if (sharesSuiteDomain(hostname)) {
    parts.push(`domain=${SHARED_COOKIE_DOMAIN}`);
  }

  if (secure) {
    parts.push('Secure');
  }

  return parts.join(';');
}

function sharesSuiteDomain(hostname: string): boolean {
  if (!hostname) {
    return false;
  }

  return hostname === '4comply.io' || hostname.endsWith(SHARED_COOKIE_DOMAIN);
}

/**
 * Module code -> the RBAC read permission ids that grant visibility of that module's sidebar
 * section. THE ONLY place this map may live (task 2.4 brief) — nothing else in the sidenav
 * duplicates it.
 *
 * A `4comply` module section spans several read-gated screens (rights, compliance inputs,
 * config, users, billing, audit), so the rule is ANY-of: the section shows if the caller holds
 * at least one of its listed read permissions, not all of them — requiring all would hide the
 * whole section from, say, an admin who only has `consent.read`.
 *
 * A module code with no entry here (a menu template ahead of this map, or a future module)
 * defaults to visible in {@link isModuleReadPermitted} — dashboard-security rule #1: this filter
 * is UX, never the authorization boundary, so failing open costs nothing the API doesn't already
 * guard, while failing closed would silently hide a whole section for a module nobody has gated
 * here yet.
 */
export const MODULE_READ_PERMISSIONS: Readonly<Record<string, readonly string[]>> = {
  '4bridge': ['bridge.read'],
  '4comply': ['consent.read', 'rights.read', 'config.read', 'users.read', 'billing.read', 'audit.read'],
  // preferences.reports.read alone (a reports-only role) must still reveal the 4Preferences section
  // so its report items show; per-item gating below then hides the config items it lacks.
  '4preferences': ['preferences.read', 'preferences.reports.read'],
  '4segments': ['segments.read'],
};

/**
 * True when the caller holds at least one of `moduleCode`'s read permissions in
 * {@link MODULE_READ_PERMISSIONS}, or when `moduleCode` has no entry in that map (fails open —
 * see the map's doc comment). `hasPermission` is injected rather than importing PermissionService
 * here so this stays a pure, synchronously-testable function; the sidenav component supplies
 * `PermissionService.has` bound to itself.
 */
export function isModuleReadPermitted(moduleCode: string, hasPermission: (permissionId: string) => boolean): boolean {
  const required = MODULE_READ_PERMISSIONS[moduleCode];

  if (!required) {
    return true;
  }

  return required.some((permissionId: string) => hasPermission(permissionId));
}

/**
 * Readiness-aware wrapper around {@link isModuleReadPermitted}: while `permissionsReady` is
 * false (the caller's effective permission set has not loaded at least once —
 * `PermissionService.ready$`), every module is treated as permitted so a fresh page load or a
 * deep link does not flash-hide every subscribed section while the fetch is still in flight. Once
 * `permissionsReady` flips true the real per-module check applies. See the sidenav component for
 * how `permissionsReady` is kept in sync with `PermissionService.ready$`.
 */
export function isModuleVisibleByPermission(
  moduleCode: string,
  permissionsReady: boolean,
  hasPermission: (permissionId: string) => boolean,
): boolean {
  if (!permissionsReady) {
    return true;
  }

  return isModuleReadPermitted(moduleCode, hasPermission);
}

/**
 * Menu-item route -> the RBAC read permission its screen requires (the exact permission that
 * item's controller enforces on GET). This is the finer, per-ITEM gate that runs under the
 * module-level {@link MODULE_READ_PERMISSIONS} check: the 4comply section shows for ANY of its
 * reads, but a caller holding only `consent.read` must still not see the config-only items (geo,
 * forms, systems, ...) or the rights-only items inside it. Roles apply on top of subscription:
 * the template already hides a module the tenant is not subscribed to (`module.isEnabled`), and
 * these two functions layer the caller's role on top of that.
 *
 * Keyed by the `Route` strings the menu template seeds (API SubscriptionSeedService). A route with
 * no entry defaults to visible (fails open — dashboard-security rule #1, same as the module map):
 * the API is the real boundary, so an unmapped or newly-added item shows rather than being
 * silently hidden. Kept in sync with the controllers' [FourComplyAuthorize(Permissions=...)] GET
 * gates. Deliberately unmapped (fail open): master-dashboard (everyone) and landing-pages
 * (config/rights-domain templates, gated config.write on write; view left open).
 */
export const ITEM_READ_PERMISSIONS: Readonly<Record<string, string>> = {
  'bridge-connections': 'bridge.read',
  'bridge-connector-metadata': 'bridge.read',
  'bridge-dashboard': 'bridge.read',
  'bridge-integrations': 'bridge.read',
  'bridge-notifications': 'bridge.read',
  'bridge-tasks': 'bridge.read',
  'bridge-webhooks': 'bridge.read',
  'business-units': 'config.read',
  'citizen-history': 'consent.read',
  'client-regulations': 'config.read',
  'compliance-inputs': 'consent.read',
  'consent-permissions': 'consent.read',
  'contact-details': 'consent.read',
  'contact-details-history': 'consent.read',
  'contact-preference': 'consent.read',
  'contact-preference-history': 'consent.read',
  'core-contact': 'preferences.read',
  'core-preference': 'preferences.read',
  'dashboard-consent': 'consent.read',
  'dashboard-rights': 'rights.read',
  'email-templates': 'config.read',
  'forgotten-users': 'rights.read',
  'forms': 'config.read',
  'geo': 'config.read',
  'hitl-config': 'rights.read',
  'hitl-tasks': 'rights.read',
  'legal-activities': 'consent.read',
  'legal-activity-errors': 'consent.read',
  'permissions': 'config.read',
  'preference-analytics': 'preferences.reports.read',
  'preference-catalog': 'preferences.read',
  'preference-channel': 'preferences.read',
  'preference-dashboard': 'preferences.reports.read',
  'preference-flow-report': 'preferences.reports.read',
  'preference-frequency': 'preferences.read',
  'preference-page': 'preferences.read',
  'preference-translations': 'preferences.read',
  'preference-webhooks': 'preferences.read',
  'preference-whitelist': 'preferences.read',
  'right-requests': 'rights.read',
  'rights-flows': 'rights.read',
  'systems': 'config.read',
  'webhooks': 'config.read',
};

/**
 * True when `route` needs no permission (unmapped — fails open, see {@link ITEM_READ_PERMISSIONS})
 * or the caller holds the one it needs.
 */
export function isItemReadPermitted(route: string, hasPermission: (permissionId: string) => boolean): boolean {
  const required = ITEM_READ_PERMISSIONS[route];

  if (!required) {
    return true;
  }

  return hasPermission(required);
}

/**
 * Readiness-aware wrapper around {@link isItemReadPermitted}, mirroring
 * {@link isModuleVisibleByPermission}: every item shows while the effective permission set has not
 * loaded yet, so a fresh load never flash-hides items; once ready the real per-item check applies.
 */
export function isItemVisibleByPermission(
  route: string,
  permissionsReady: boolean,
  hasPermission: (permissionId: string) => boolean,
): boolean {
  if (!permissionsReady) {
    return true;
  }

  return isItemReadPermitted(route, hasPermission);
}
